'use client';
import { useState, useCallback, useRef } from 'react';
import { Accessibility, RefreshCw, Download, CheckCircle, XCircle, AlertTriangle, Upload, FileText, Sparkles, HardDrive } from 'lucide-react';
import { useTheme } from '@/components/ThemeContext';
import { AgentPageLayout, AgentInputPanel, AgentLabel, AgentStreamOutput, AgentEmptyState } from '@/components/AgentPageLayout';
import { PROJECT_TYPES, AGENT_CONFIGS, downloadAsTextFile } from '@/lib/agent-constants';
import { NasProjectSelector, useNasAnalysis } from '@/components/NasProjectSelector';
import { AgentToolbar } from '@/components/AgentToolbar';

interface BFItem { id: string; category: string; item: string; standard: string; design: string; status: 'pass' | 'fail' | 'warn'; note: string; }

type InputMode = 'info' | 'upload' | 'nas';

export default function BFAgentPage() {
    const { theme } = useTheme();
    const isDark = theme === 'dark';
    const config = AGENT_CONFIGS.bf;
    const [items, setItems] = useState<BFItem[]>([]);
    const [isProcessing, setIsProcessing] = useState(false);
    const [inputMode, setInputMode] = useState<InputMode>('nas');
    const [aiStream, setAiStream] = useState('');
    const [aiDone, setAiDone] = useState(false);
    const [projectType, setProjectType] = useState('학교');
    const [projectName, setProjectName] = useState('');
    const [totalArea, setTotalArea] = useState('');
    const [floors, setFloors] = useState('');
    const [file, setFile] = useState<File | null>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const nasAnalysis = useNasAnalysis();

    const handleNasAnalyze = useCallback(async (projectId: string, selectedFiles: string[], agentType: string) => {
        setItems([]);
        const fullText = await nasAnalysis.analyze(projectId, selectedFiles, 'bf', `프로젝트 유형: ${projectType}`);
        if (fullText) {
            try {
                const jsonMatch = fullText.match(/\{[\s\S]*\}/);
                if (jsonMatch) {
                    const parsed = JSON.parse(jsonMatch[0]);
                    if (parsed.items && Array.isArray(parsed.items)) {
                        setItems(parsed.items.map((item: any, idx: number) => ({
                            id: item.id || String(idx + 1), category: item.category || '기타', item: item.item || '',
                            standard: item.standard || '', design: item.design || '', status: item.status || 'warn', note: item.note || ''
                        })));
                    }
                }
            } catch { }
        }
    }, [nasAnalysis, projectType]);

    const checkBF = useCallback(async () => {
        setIsProcessing(true);
        setAiStream('');
        setAiDone(false);
        setItems([]);

        try {
            let fetchOptions: RequestInit;

            if (inputMode === 'upload' && file) {
                const formData = new FormData();
                formData.append('file', file);
                formData.append('projectInfo', JSON.stringify({ projectType, projectName, totalArea, floors }));
                fetchOptions = { method: 'POST', body: formData };
            } else {
                const body = { projectType, projectName: projectName || `${projectType} 신축 프로젝트`, totalArea: totalArea || '5000', floors: floors || '3', analysisType: 'BF인증 전수검토' };
                fetchOptions = { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) };
            }

            const res = await fetch('/api/bf-agent', fetchOptions);
            if (!res.ok) throw new Error('API 오류');

            const reader = res.body?.getReader();
            if (!reader) throw new Error('스트림 없음');

            const decoder = new TextDecoder();
            let fullText = '';

            while (true) {
                const { done, value } = await reader.read();
                if (done) break;
                const chunk = decoder.decode(value, { stream: true });
                for (const line of chunk.split('\n')) {
                    if (line.startsWith('data: ')) {
                        try {
                            const data = JSON.parse(line.substring(6));
                            if (data.type === 'chunk') { fullText += data.text; setAiStream(fullText); }
                            else if (data.type === 'complete') { fullText = data.fullText; setAiStream(fullText); }
                        } catch { /* skip */ }
                    }
                }
            }

            try {
                const jsonMatch = fullText.match(/\{[\s\S]*\}/);
                if (jsonMatch) {
                    const parsed = JSON.parse(jsonMatch[0]);
                    if (parsed.items && Array.isArray(parsed.items)) {
                        setItems(parsed.items.map((item: any, idx: number) => ({
                            id: item.id || String(idx + 1), category: item.category || '기타', item: item.item || '',
                            standard: item.standard || '', design: item.design || '', status: item.status || 'warn', note: item.note || ''
                        })));
                    }
                }
            } catch { /* AI raw text mode */ }

            setAiDone(true);
        } catch (err: any) {
            setAiStream(`오류: ${err.message}`);
            setAiDone(true);
        }
        setIsProcessing(false);
    }, [inputMode, file, projectType, projectName, totalArea, floors]);

    const pass = items.filter(i => i.status === 'pass').length;
    const fail = items.filter(i => i.status === 'fail').length;
    const warn = items.filter(i => i.status === 'warn').length;

    const handleExport = () => {
        if (!aiStream) return;
        const filename = `BF_검토결과_${projectName || projectType}_${new Date().toISOString().slice(0,10)}.txt`;
        downloadAsTextFile(aiStream, filename);
    };

    return (
        <AgentPageLayout config={config} icon={<Accessibility className="w-5 h-5" />} isProcessing={isProcessing} aiDone={aiDone}>
            {/* Input Mode Tabs */}
            <div className="flex gap-2 mb-4">
                {[
                    { mode: 'nas' as InputMode, icon: <HardDrive className="w-3.5 h-3.5" />, label: 'NAS 프로젝트' },
                    { mode: 'info' as InputMode, icon: <FileText className="w-3.5 h-3.5" />, label: '프로젝트 정보' },
                    { mode: 'upload' as InputMode, icon: <Upload className="w-3.5 h-3.5" />, label: 'PDF 업로드' },
                ].map(tab => (
                    <button key={tab.mode} onClick={() => setInputMode(tab.mode)}
                        className={`flex items-center gap-1.5 px-4 py-2 rounded-xl text-xs font-bold transition cursor-pointer ${
                            inputMode === tab.mode
                                ? `${config.accentBg} ${config.accentText} border ${config.accentBorder}`
                                : isDark ? 'bg-white/5 text-gray-500 border border-white/10' : 'bg-gray-50 text-gray-400 border border-gray-200'
                        }`}>
                        {tab.icon}{tab.label}
                    </button>
                ))}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
                {/* Input Panel */}
                <div className="lg:col-span-4">
                    <AgentInputPanel>
                        {inputMode === 'nas' ? (
                            <NasProjectSelector agentType="bf" accentBg={config.accentBg} accentText={config.accentText} accentBorder={config.accentBorder} gradient={config.gradient} onAnalyze={handleNasAnalyze} isProcessing={nasAnalysis.isProcessing}>
                                <div>
                                    <AgentLabel>건물 용도</AgentLabel>
                                    <div className="grid grid-cols-4 gap-1.5 mt-1.5">
                                        {PROJECT_TYPES.slice(0, 4).map(t => (
                                            <button key={t} onClick={() => setProjectType(t)}
                                                className={`py-1.5 rounded-lg text-[10px] font-bold cursor-pointer transition ${projectType === t ? `${config.accentBg} ${config.accentText} border ${config.accentBorder}` : isDark ? 'bg-white/5 text-gray-500 border border-white/10' : 'bg-gray-50 text-gray-400 border border-gray-200'}`}>{t}</button>
                                        ))}
                                    </div>
                                </div>
                            </NasProjectSelector>
                        ) : inputMode === 'info' ? (
                            <>
                                <div>
                                    <AgentLabel>프로젝트 유형</AgentLabel>
                                    <div className="grid grid-cols-4 gap-1.5 mt-1.5">
                                        {PROJECT_TYPES.map(t => (
                                            <button key={t} onClick={() => setProjectType(t)}
                                                className={`py-2 rounded-lg text-[10px] font-bold cursor-pointer transition ${
                                                    projectType === t
                                                        ? `${config.accentBg} ${config.accentText} border ${config.accentBorder}`
                                                        : isDark ? 'bg-white/5 text-gray-500 border border-white/10' : 'bg-gray-50 text-gray-400 border border-gray-200'
                                                }`}>{t}</button>
                                        ))}
                                    </div>
                                </div>
                                <div>
                                    <AgentLabel>프로젝트명</AgentLabel>
                                    <input value={projectName} onChange={e => setProjectName(e.target.value)} placeholder="예: 웅촌초등학교 BTL"
                                        className={`w-full mt-1.5 px-3 py-2.5 rounded-lg text-xs ${isDark ? 'bg-white/5 border border-white/10 text-white placeholder-gray-600' : 'bg-gray-50 border border-gray-200 text-gray-900 placeholder-gray-400'}`} />
                                </div>
                                <div className="grid grid-cols-2 gap-2">
                                    <div>
                                        <AgentLabel>연면적 (㎡)</AgentLabel>
                                        <input value={totalArea} onChange={e => setTotalArea(e.target.value)} placeholder="5000" type="number"
                                            className={`w-full mt-1.5 px-3 py-2.5 rounded-lg text-xs ${isDark ? 'bg-white/5 border border-white/10 text-white placeholder-gray-600' : 'bg-gray-50 border border-gray-200 text-gray-900 placeholder-gray-400'}`} />
                                    </div>
                                    <div>
                                        <AgentLabel>층수</AgentLabel>
                                        <input value={floors} onChange={e => setFloors(e.target.value)} placeholder="3" type="number"
                                            className={`w-full mt-1.5 px-3 py-2.5 rounded-lg text-xs ${isDark ? 'bg-white/5 border border-white/10 text-white placeholder-gray-600' : 'bg-gray-50 border border-gray-200 text-gray-900 placeholder-gray-400'}`} />
                                    </div>
                                </div>
                                <button onClick={checkBF} disabled={isProcessing}
                                    className={`w-full py-3 rounded-xl bg-gradient-to-r ${config.gradient} text-white text-xs font-bold cursor-pointer disabled:opacity-40 flex items-center justify-center gap-2 shadow-lg`}>
                                    {isProcessing ? <><RefreshCw className="w-4 h-4 animate-spin" />AI 분석 중...</> : <><Sparkles className="w-4 h-4" />BF 인증 AI 자동 검토</>}
                                </button>
                            </>
                        ) : (
                            <>
                                <div className={`border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition ${
                                    file
                                        ? isDark ? 'border-sky-500/30 bg-sky-500/5' : 'border-sky-300 bg-sky-50'
                                        : isDark ? 'border-white/10 hover:border-sky-500/30' : 'border-gray-200 hover:border-sky-300'
                                }`} onClick={() => fileInputRef.current?.click()}>
                                    <Upload className={`w-8 h-8 mx-auto mb-2 ${isDark ? 'text-sky-500/30' : 'text-sky-300'}`} />
                                    <p className="text-xs font-bold">{file ? file.name : 'PDF 도면 파일을 선택하세요'}</p>
                                    <input ref={fileInputRef} type="file" accept=".pdf" className="hidden" onChange={e => setFile(e.target.files?.[0] || null)} />
                                </div>
                                <button onClick={checkBF} disabled={isProcessing}
                                    className={`w-full py-3 rounded-xl bg-gradient-to-r ${config.gradient} text-white text-xs font-bold cursor-pointer disabled:opacity-40 flex items-center justify-center gap-2 shadow-lg`}>
                                    {isProcessing ? <><RefreshCw className="w-4 h-4 animate-spin" />AI 분석 중...</> : <><Sparkles className="w-4 h-4" />BF 인증 AI 자동 검토</>}
                                </button>
                            </>
                        )}
                    </AgentInputPanel>
                </div>

                {/* Results Panel */}
                <div className="lg:col-span-8 space-y-4">
                    {items.length > 0 && (
                        <>
                            {/* Summary Cards */}
                            <div className="grid grid-cols-3 gap-3">
                                {[
                                    { label: '적합', value: pass, icon: <CheckCircle className="w-5 h-5" />, color: 'text-emerald-400', bg: isDark ? 'bg-emerald-500/10 border-emerald-500/20' : 'bg-emerald-50 border-emerald-200' },
                                    { label: '주의', value: warn, icon: <AlertTriangle className="w-5 h-5" />, color: 'text-amber-400', bg: isDark ? 'bg-amber-500/10 border-amber-500/20' : 'bg-amber-50 border-amber-200' },
                                    { label: '부적합', value: fail, icon: <XCircle className="w-5 h-5" />, color: 'text-red-400', bg: isDark ? 'bg-red-500/10 border-red-500/20' : 'bg-red-50 border-red-200' },
                                ].map((s, i) => (
                                    <div key={i} className={`rounded-xl border p-3 flex items-center gap-3 ${s.bg}`}>
                                        <div className={s.color}>{s.icon}</div>
                                        <div>
                                            <div className={`text-[10px] ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{s.label}</div>
                                            <div className={`text-xl font-bold ${s.color}`}>{s.value} <span className="text-[10px] font-normal">/ {items.length}</span></div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                            {/* Results Table */}
                            <div className={`rounded-2xl border overflow-hidden ${isDark ? 'bg-gray-900/80 border-white/10' : 'bg-white border-gray-200 shadow-sm'}`}>
                                <div className={`px-4 py-2.5 border-b flex items-center justify-between ${isDark ? 'border-white/10 bg-white/[0.02]' : 'border-gray-100 bg-gray-50'}`}>
                                    <span className="text-xs font-bold">BF 인증 체크리스트 (AI 분석 결과)</span>
                                    <button onClick={handleExport} className={`text-[10px] px-3 py-1.5 rounded-lg cursor-pointer flex items-center gap-1 font-bold transition hover:opacity-80 ${isDark ? 'bg-sky-500/20 text-sky-300' : 'bg-sky-50 text-sky-600'}`}>
                                        <Download className="w-3 h-3" />내보내기
                                    </button>
                                </div>
                                <table className="w-full text-[10px]">
                                    <thead><tr className={isDark ? 'bg-white/[0.03]' : 'bg-gray-50'}>
                                        {['결과', '분류', '항목', '기준', '설계값', '비고'].map(h => <th key={h} className={`px-3 py-2.5 font-bold text-left ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{h}</th>)}
                                    </tr></thead>
                                    <tbody>{items.map(item => (
                                        <tr key={item.id} className={`border-t ${isDark ? 'border-white/5 hover:bg-white/[0.03]' : 'border-gray-100 hover:bg-gray-50'} transition`}>
                                            <td className="px-3 py-2.5">{item.status === 'pass' ? <CheckCircle className="w-3.5 h-3.5 text-emerald-400" /> : item.status === 'warn' ? <AlertTriangle className="w-3.5 h-3.5 text-amber-400" /> : <XCircle className="w-3.5 h-3.5 text-red-400" />}</td>
                                            <td className="px-3 py-2.5"><span className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${isDark ? 'bg-white/5' : 'bg-gray-100'}`}>{item.category}</span></td>
                                            <td className="px-3 py-2.5 font-semibold">{item.item}</td>
                                            <td className={`px-3 py-2.5 font-mono ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>{item.standard}</td>
                                            <td className={`px-3 py-2.5 font-mono font-bold ${item.status === 'pass' ? 'text-emerald-400' : item.status === 'warn' ? 'text-amber-400' : 'text-red-400'}`}>{item.design}</td>
                                            <td className={`px-3 py-2.5 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>{item.note}</td>
                                        </tr>
                                    ))}</tbody>
                                </table>
                            </div>
                        </>
                    )}

                    <AgentStreamOutput aiStream={inputMode === 'nas' ? nasAnalysis.aiStream : aiStream} isProcessing={inputMode === 'nas' ? nasAnalysis.isProcessing : isProcessing} accentColor="text-sky-400" />
                    {(inputMode === 'nas' ? nasAnalysis.aiStream : aiStream) && !(inputMode === 'nas' ? nasAnalysis.isProcessing : isProcessing) && (
                        <AgentToolbar title="BF 인증 검토" content={inputMode === 'nas' ? nasAnalysis.aiStream : aiStream} agentType="bf" />
                    )}

                    {!(inputMode === 'nas' ? nasAnalysis.aiStream : aiStream) && !(inputMode === 'nas' ? nasAnalysis.isProcessing : isProcessing) && items.length === 0 && (
                        <AgentEmptyState
                            icon={<Accessibility className={`w-8 h-8 ${isDark ? 'text-sky-500/30' : 'text-sky-200'}`} />}
                            message={inputMode === 'nas' ? 'NAS 프로젝트를 선택하고 BF 인증 검토를 시작하세요' : '프로젝트 정보를 입력하고 AI BF 인증 검토를 시작하세요'}
                        />
                    )}
                </div>
            </div>
        </AgentPageLayout>
    );
}
